import styles from './InvestigationAssetHealth.module.scss';
import { GeofenceData } from '@customer/hooks/assets/useAssetGeofenceTableData';
import GeofenceStatus from '@customer/components/asset-data/geofence-status/GeofenceStatus';
import { useTranslation } from 'react-i18next';
import { useAbsoluteTime } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';
import { AccordionStatus } from '@customer/components/accordions/types';
import CasAccordion from '@customer/components/accordions/accordion/CasAccordion';
import { AssetConfigurationAccordion } from '@customer/store/slices/globalSlice';
import AccordionHeader from '@customer/components/accordions/header/AccordionHeader';
import AccordionTable from '@customer/components/accordions/table/AccordionTable';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { useState } from 'react';
import { ColDef } from 'ag-grid-community';
import { AssetFaultBasicModel } from '@customer/store/apis/assetsApi';
import {
  columnDefAssetFaultBasicCategory,
  columnDefAssetFaultBasicLevel,
  columnDefAssetFaultBasicName,
} from '@customer/components/tables/grid/columnDefs';

export interface InvestigationAssetHealthProps {
  ts?: string;
  geofenceData: GeofenceData[];
  geofenceUpToDate: boolean;
  configUpToDate: boolean | null;
  faults: {
    count: number;
    items: AssetFaultBasicModel[];
  };
}

export function InvestigationAssetHealth({
  ts,
  geofenceData,
  geofenceUpToDate,
  configUpToDate,
  faults,
}: InvestigationAssetHealthProps) {
  const { t } = useTranslation();

  const interactionTime = useAbsoluteTime(ts);

  const [columnDefs] = useState<ColDef<AssetFaultBasicModel>[]>(() => [
    { ...columnDefAssetFaultBasicCategory(t) },
    { ...columnDefAssetFaultBasicName(t) },
    { ...columnDefAssetFaultBasicLevel(t) },
  ]);

  const getRowId = (data: AssetFaultBasicModel) => data.flt.toString();

  const selfTestGood = faults.count === 0;

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={styles.row__label}>{t('ShowingInteractionDataAt')}</div>
        <div className={styles.row__value}>{interactionTime}</div>
      </div>

      <div className={styles.accordions}>
        <CasAccordion
          key="self-test"
          defaultExpanded={!selfTestGood}
          status={selfTestGood ? AccordionStatus.Healthy : AccordionStatus.Danger}
          icon={selfTestGood ? 'good' : 'information'}
          header={
            <AccordionHeader
              title={t('SelfTest')}
              subtitle={
                selfTestGood
                  ? t('NoActiveFaults')
                  : t('ActiveFaultWithCount', { count: faults.count })
              }
            />
          }
          hideControl={selfTestGood}
        >
          <AccordionTable hasRows={faults.items.length > 0}>
            <GridControlled<AssetFaultBasicModel>
              autoheight={true}
              sliceKey="InvestigationAssetPanelSelfTest"
              rowData={faults.items}
              columnDefs={columnDefs}
              getRowId={getRowId}
            />
          </AccordionTable>
        </CasAccordion>

        {configUpToDate !== null && (
          <CasAccordion
            key={AssetConfigurationAccordion.SoftwareStatus}
            defaultExpanded={false}
            status={configUpToDate ? AccordionStatus.Healthy : AccordionStatus.Danger}
            icon={configUpToDate ? 'good' : 'information'}
            header={
              <AccordionHeader
                title={t('Software Status')}
                subtitle={configUpToDate ? t('LatestConfiguration') : t('OutdatedVersion')}
              />
            }
            hideControl={true}
          />
        )}

        <GeofenceStatus
          geofenceData={geofenceData}
          good={geofenceUpToDate}
          expanded={!geofenceUpToDate}
          transGood="GeofenceSiteVersion"
          transBad="OutdatedVersion"
        />
      </div>
    </div>
  );
}

export default InvestigationAssetHealth;
